import { FC, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowDownCircle, ArrowUpCircle, ChevronLeft } from 'lucide-react';
import { TOKENS, PageCard, SectionLabel, Chip } from '../../components/design-system';

type Lang = 'curl' | 'js' | 'python' | 'cpp';

const LANG_LABELS: Record<Lang, string> = {
  curl: 'curl',
  js: 'JavaScript',
  python: 'Python',
  cpp: 'C++ (ESP32)',
};

const CodeBlock: FC<{ children: string; lang?: string }> = ({ children, lang }) => (
  <pre
    style={{
      color: '#e6edf3',
      backgroundColor: '#0d1117',
      fontSize: 12.5,
      lineHeight: 1.55,
      overflowX: 'auto',
      whiteSpace: 'pre',
      padding: 16,
      borderRadius: TOKENS.radius.card,
      margin: 0,
      marginBottom: TOKENS.spacing.lg,
    }}
  >
    <code data-lang={lang}>{children}</code>
  </pre>
);

const Section: FC<{ id: string; title: string; children: React.ReactNode }> = ({
  id,
  title,
  children,
}) => (
  <section id={id} style={{ marginBottom: TOKENS.spacing.xxl }}>
    <h2 style={{
      fontSize: 22,
      fontWeight: 700,
      letterSpacing: -0.3,
      color: TOKENS.color.text,
      marginTop: 0,
      marginBottom: TOKENS.spacing.md,
    }}>{title}</h2>
    {children}
  </section>
);

type Dir = 'in' | 'out';
const MessageCard: FC<{ name: string; dir: Dir; children: React.ReactNode }> = ({ name, dir, children }) => {
  const isIn = dir === 'in';
  return (
    <div style={{
      border: `1px solid ${TOKENS.color.border}`,
      borderRadius: TOKENS.radius.card,
      background: TOKENS.color.card,
      marginBottom: TOKENS.spacing.lg,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: TOKENS.spacing.md,
        padding: '14px 18px',
        borderBottom: `1px solid ${TOKENS.color.border}`,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {isIn ? <ArrowDownCircle size={16} color={TOKENS.color.severity.low.text} /> : <ArrowUpCircle size={16} color={TOKENS.color.action} />}
            <code style={{ fontSize: 14, fontWeight: 700, color: TOKENS.color.text }}>{name}</code>
          </div>
          <span style={{ marginTop: 4, fontSize: 12, color: TOKENS.color.muted }}>
            {isIn ? 'servidor → dispositivo' : 'dispositivo → servidor'}
          </span>
        </div>
        <Chip tone={isIn ? 'low' : 'action'}>{isIn ? 'Recebido' : 'Enviado'}</Chip>
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  );
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: 13,
  color: TOKENS.color.text,
};

const thStyle: React.CSSProperties = {
  fontSize: TOKENS.typography.label.size,
  fontWeight: TOKENS.typography.label.weight,
  textTransform: TOKENS.typography.label.transform,
  letterSpacing: TOKENS.typography.label.tracking,
  color: TOKENS.color.muted,
  background: TOKENS.color.surface,
  textAlign: 'left',
  padding: '10px 14px',
  borderBottom: `1px solid ${TOKENS.color.border}`,
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderBottom: `1px solid ${TOKENS.color.divider}`,
  fontSize: 13,
};

const TutorialWebSocketPage: FC = () => {
  const apiUrl = process.env.REACT_APP_API_URL ?? 'https://api.cmblindada.com.br';
  const wsUrl = process.env.REACT_APP_WS_URL ?? 'wss://ws.cmblindada.com.br';

  const [quickstartLang, setQuickstartLang] = useState<Lang>('curl');

  const examples = useMemo(
    () => ({
      curl: {
        provision: `# 1. Provisionar um token de dispositivo
curl -X POST "${apiUrl}/devices/<BLUETOOTH_ID>/provision" \\
  -H "Authorization: Bearer <SEU_ADMIN_JWT>" \\
  -H "Content-Type: application/json" \\
  -d '{"notes":"primeiro provisionamento"}'

# Resposta:
# { "token": "eyJhbGciOiJIUzI1NiIs...", "jti": "uuid-v4" }`,
        connect: `# 2. Testar conexão com websocat (ou wscat -c)
websocat "${wsUrl}/?token=<TOKEN_DO_DISPOSITIVO>"

# 3. Enviar um heartbeat após receber o "welcome"
{"type":"heartbeat","payload":{"firmware_version":"1.0.0","uptime_s":120,"rssi":-65}}`,
      },
      js: {
        provision: `// 1. Provisionar um token de dispositivo
const res = await fetch(\`${apiUrl}/devices/\${bluetoothId}/provision\`, {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${adminJwt}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ notes: 'primeiro provisionamento' }),
});
const { token } = await res.json();`,
        connect: `// 2. Conectar ao gateway
const ws = new WebSocket(\`${wsUrl}/?token=\${token}\`);

ws.addEventListener('open', () => {
  console.log('conectado');
});

ws.addEventListener('message', (ev) => {
  const msg = JSON.parse(ev.data);
  console.log('recebido:', msg);

  if (msg.type === 'welcome') {
    // 3. Enviar heartbeat após autenticar
    ws.send(JSON.stringify({
      type: 'heartbeat',
      payload: { firmware_version: '1.0.0', uptime_s: 120, rssi: -65 },
    }));
  }
});

ws.addEventListener('close', (ev) => {
  console.log('fechado:', ev.code, ev.reason);
});`,
      },
      python: {
        provision: `# pip install requests websockets
import requests

# 1. Provisionar um token de dispositivo
res = requests.post(
    f"${apiUrl}/devices/{bluetooth_id}/provision",
    headers={"Authorization": f"Bearer {admin_jwt}"},
    json={"notes": "primeiro provisionamento"},
)
res.raise_for_status()
token = res.json()["token"]`,
        connect: `import asyncio
import json
import websockets

async def device_loop(token: str):
    uri = f"${wsUrl}/?token={token}"
    async with websockets.connect(uri) as ws:
        async for raw in ws:
            msg = json.loads(raw)
            print("recebido:", msg)

            if msg["type"] == "welcome":
                # 3. Enviar heartbeat
                await ws.send(json.dumps({
                    "type": "heartbeat",
                    "payload": {
                        "firmware_version": "1.0.0",
                        "uptime_s": 120,
                        "rssi": -65,
                    },
                }))

asyncio.run(device_loop(token))`,
      },
      cpp: {
        provision: `// Pré-requisito: o token de dispositivo é gerado uma vez pelo back-office
// (provisionamento via API) e gravado em NVS/flash do ESP32.
// O firmware NÃO chama o endpoint de provisão diretamente.

#include <Preferences.h>

Preferences prefs;
prefs.begin("cmb", true);
String token = prefs.getString("ws_token", "");
prefs.end();`,
        connect: `// Lib: WebSockets (Markus Sattler) - https://github.com/Links2004/arduinoWebSockets
#include <WiFi.h>
#include <WebSocketsClient.h>
#include <ArduinoJson.h>

WebSocketsClient ws;

void onEvent(WStype_t type, uint8_t *payload, size_t length) {
  switch (type) {
    case WStype_CONNECTED:
      Serial.println("[WS] conectado");
      break;
    case WStype_TEXT: {
      StaticJsonDocument<512> doc;
      deserializeJson(doc, payload, length);
      const char *msgType = doc["type"];
      Serial.printf("[WS] msg: %s\\n", msgType);

      if (strcmp(msgType, "welcome") == 0) {
        // Enviar heartbeat
        StaticJsonDocument<256> hb;
        hb["type"] = "heartbeat";
        JsonObject p = hb.createNestedObject("payload");
        p["firmware_version"] = "1.0.0";
        p["uptime_s"] = millis() / 1000;
        p["rssi"] = WiFi.RSSI();
        String out;
        serializeJson(hb, out);
        ws.sendTXT(out);
      }
      break;
    }
    case WStype_DISCONNECTED:
      Serial.println("[WS] desconectado");
      break;
    default: break;
  }
}

void setup() {
  // ... WiFi.begin(...)
  String url = "/?token=" + String(deviceToken);
  ws.beginSSL("ws.cmblindada.com.br", 443, url.c_str());
  ws.onEvent(onEvent);
  ws.setReconnectInterval(5000);
}

void loop() {
  ws.loop();
}`,
      },
    }),
    [apiUrl, wsUrl],
  );

  const tocItems = [
    { id: 'overview', label: 'Visão geral' },
    { id: 'endpoints', label: 'Endpoints' },
    { id: 'auth', label: 'Autenticação' },
    { id: 'quickstart', label: 'Quickstart' },
    { id: 'messages', label: 'Mensagens' },
    { id: 'close-codes', label: 'Códigos de fechamento' },
    { id: 'heartbeat', label: 'Heartbeat / ping' },
    { id: 'playground', label: 'Playground' },
    { id: 'troubleshooting', label: 'Troubleshooting' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 260px) 1fr', gap: TOKENS.spacing.xl }}>
      {/* TOC */}
      <aside>
        <div style={{ position: 'sticky', top: 90 }}>
          <PageCard>
            <SectionLabel>Nesta página</SectionLabel>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginTop: TOKENS.spacing.md, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {tocItems.map((t) => (
                <li key={t.id}>
                  <a href={`#${t.id}`} style={{ display: 'block', padding: '6px 8px', fontSize: 13, color: TOKENS.color.muted, textDecoration: 'none', borderRadius: TOKENS.radius.chip }}>
                    {t.label}
                  </a>
                </li>
              ))}
            </ul>
          </PageCard>
        </div>
      </aside>

      {/* Content */}
      <div>
        <PageCard style={{ marginBottom: TOKENS.spacing.xl }}>
          <SectionLabel>Documentação</SectionLabel>
          <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: -0.5, color: TOKENS.color.text, margin: 0, marginTop: TOKENS.spacing.sm }}>
            Tutorial WebSocket
          </h1>
          <p style={{ marginTop: TOKENS.spacing.sm, fontSize: 14, color: TOKENS.color.muted, lineHeight: 1.55, marginBottom: 0 }}>
            Como integrar dispositivos e aplicações ao <strong>CMB WebSocket Gateway</strong>{' '}
            para envio de heartbeats, alarmes em tempo real e recepção de comandos.
          </p>
        </PageCard>

        <Section id='overview' title='Visão geral'>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
            O Gateway é um servidor WebSocket que mantém uma conexão persistente entre as
            caixas de medição (ou aplicações cliente) e a plataforma CMB. Ele é responsável por:
          </p>
          <ul style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.7, paddingLeft: 20 }}>
            <li>Receber <strong>heartbeats</strong> periódicos dos dispositivos para indicar que estão online.</li>
            <li>Receber e processar <strong>eventos de alarme</strong> em tempo real (porta, vibração, energia etc.).</li>
            <li>Entregar <strong>comandos</strong> da plataforma para dispositivos conectados.</li>
            <li>Confirmar a execução dos comandos via <strong>command_ack</strong>.</li>
            <li>Fazer <em>push</em> de alterações para o painel web em tempo real.</li>
          </ul>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
            Toda comunicação usa frames JSON com o envelope <code>{`{ "type": "<tipo>", "payload": { ... } }`}</code>.
          </p>
        </Section>

        <Section id='endpoints' title='Endpoints'>
          <div style={{ border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.card, overflow: 'hidden', background: TOKENS.color.card }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Recurso</th>
                  <th style={thStyle}>URL</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tdStyle}>API REST (provisão / revogação de token)</td>
                  <td style={tdStyle}><code>{apiUrl}</code></td>
                </tr>
                <tr>
                  <td style={tdStyle}>Gateway WebSocket (dispositivo)</td>
                  <td style={tdStyle}><code>{wsUrl}/</code></td>
                </tr>
                <tr>
                  <td style={tdStyle}>Gateway WebSocket (cliente web)</td>
                  <td style={tdStyle}><code>{wsUrl}/web</code></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p style={{ marginTop: TOKENS.spacing.sm, color: TOKENS.color.muted, fontSize: 12 }}>
            Os endereços acima refletem a configuração atual do ambiente em que você está
            autenticado (<code>REACT_APP_API_URL</code> / <code>REACT_APP_WS_URL</code>).
          </p>
        </Section>

        <Section id='auth' title='Autenticação'>
          <h4 style={{ fontSize: 15, fontWeight: 700, color: TOKENS.color.text, marginTop: TOKENS.spacing.lg, marginBottom: TOKENS.spacing.sm }}>Token de dispositivo</h4>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
            Cada caixa tem um JWT longo, emitido <strong>uma vez</strong> pelo endpoint
            de provisionamento e gravado no firmware. O token é assinado em HS256 e contém:
          </p>
          <CodeBlock lang='json'>{`{
  "sub": "<bluetooth_id>",   // identificador físico da caixa
  "box_id": 123,             // ID interno do measuring_box
  "jti": "<uuid>",           // identificador único para revogação
  "iat": 1700000000          // emitido em (opcional)
}`}</CodeBlock>

          <h4 style={{ fontSize: 15, fontWeight: 700, color: TOKENS.color.text, marginTop: TOKENS.spacing.lg, marginBottom: TOKENS.spacing.sm }}>Como enviar o token</h4>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>O gateway aceita o JWT de duas formas:</p>
          <ul style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.7, paddingLeft: 20 }}>
            <li><strong>Cabeçalho HTTP</strong> (preferido, para clientes que controlam o handshake): <code>Authorization: Bearer &lt;jwt&gt;</code></li>
            <li><strong>Query string</strong> (fallback para WebSocket nativo do browser): <code>?token=&lt;jwt&gt;</code></li>
          </ul>

          <h4 style={{ fontSize: 15, fontWeight: 700, color: TOKENS.color.text, marginTop: TOKENS.spacing.lg, marginBottom: TOKENS.spacing.sm }}>Revogação</h4>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
            Tokens podem ser revogados a qualquer momento pelo endpoint{' '}
            <code>POST /devices/&lt;bluetooth_id&gt;/revoke/&lt;jti&gt;</code>.
            Após revogado, a próxima reconexão será rejeitada com o código{' '}
            <strong>4403</strong>.
          </p>
        </Section>

        <Section id='quickstart' title='Quickstart'>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6, marginBottom: TOKENS.spacing.lg }}>
            Quatro passos pra ter um dispositivo conectado e enviando dados:
          </p>
          <ol style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.7, paddingLeft: 20, marginBottom: TOKENS.spacing.lg }}>
            <li><strong>Provisione</strong> um token via API (precisa de um JWT de admin).</li>
            <li><strong>Grave o token</strong> no firmware ou guarde em ambiente seguro.</li>
            <li><strong>Conecte</strong> no gateway WebSocket usando o token.</li>
            <li>Aguarde o frame <strong>welcome</strong> e comece a enviar mensagens.</li>
          </ol>

          <div style={{ display: 'flex', gap: 6, marginBottom: TOKENS.spacing.lg, flexWrap: 'wrap' }}>
            {(Object.keys(LANG_LABELS) as Lang[]).map((l) => {
              const active = quickstartLang === l;
              return (
                <button
                  key={l}
                  type='button'
                  onClick={() => setQuickstartLang(l)}
                  style={{
                    padding: '6px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    border: `1px solid ${active ? TOKENS.color.action : TOKENS.color.border}`,
                    background: active ? TOKENS.color.actionSoft : TOKENS.color.card,
                    color: active ? TOKENS.color.action : TOKENS.color.muted,
                    borderRadius: TOKENS.radius.chip,
                    cursor: 'pointer',
                  }}
                >
                  {LANG_LABELS[l]}
                </button>
              );
            })}
          </div>

          <h5 style={{ fontSize: 13, fontWeight: 700, color: TOKENS.color.text, marginBottom: TOKENS.spacing.sm }}>1 e 2 — Provisão do token</h5>
          <CodeBlock lang={quickstartLang}>{examples[quickstartLang].provision}</CodeBlock>

          <h5 style={{ fontSize: 13, fontWeight: 700, color: TOKENS.color.text, marginBottom: TOKENS.spacing.sm }}>3 e 4 — Conexão e primeira mensagem</h5>
          <CodeBlock lang={quickstartLang}>{examples[quickstartLang].connect}</CodeBlock>
        </Section>

        <Section id='messages' title='Referência de mensagens'>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6, marginBottom: TOKENS.spacing.lg }}>
            Todas as mensagens seguem o envelope{' '}
            <code>{`{ "type": "<tipo>", "payload": { ... } }`}</code>.
          </p>

          <MessageCard name='welcome' dir='in'>
            <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
              Enviado pelo gateway logo após uma conexão autenticada com sucesso.
            </p>
            <CodeBlock lang='json'>{`{
  "type": "welcome",
  "payload": {
    "bluetooth_id": "AA:BB:CC:DD:EE:FF",
    "server_time": "2025-02-15T10:30:45.123Z"
  }
}`}</CodeBlock>
          </MessageCard>

          <MessageCard name='heartbeat' dir='out'>
            <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
              Sinaliza que a caixa está online. Recomenda-se enviar a cada 30s. O servidor
              atualiza <code>last_seen_at</code> e propaga métricas opcionais.
            </p>
            <CodeBlock lang='json'>{`{
  "type": "heartbeat",
  "payload": {
    "firmware_version": "2.1.4",  // opcional
    "uptime_s": 3600,              // opcional
    "rssi": -65                    // opcional
  }
}`}</CodeBlock>
          </MessageCard>

          <MessageCard name='alarm_event' dir='out'>
            <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
              Notifica mudança de estado de um sensor de alarme. O servidor grava o evento
              e faz broadcast para os clientes web conectados.
            </p>
            <CodeBlock lang='json'>{`{
  "type": "alarm_event",
  "payload": {
    "alarm_type": "door",                // ver lista abaixo
    "active": true,                       // true = disparou, false = limpou
    "ts": "2025-02-15T10:32:10.500Z"     // opcional (default: agora)
  }
}`}</CodeBlock>
            <p style={{ color: TOKENS.color.text, fontSize: 13, marginBottom: 6 }}><strong>Valores aceitos em <code>alarm_type</code>:</strong></p>
            <p style={{ color: TOKENS.color.text, fontSize: 13, lineHeight: 1.6 }}>
              <code>door</code>, <code>bolt</code>, <code>door_lock</code>, <code>vibration</code>,{' '}
              <code>energy</code>, <code>sound</code>, <code>violation</code>,{' '}
              <code>request_opening_of_safe</code>, <code>maximum_time_for_open_door</code>,{' '}
              <code>cable_cut_alarm</code>
            </p>
          </MessageCard>

          <MessageCard name='command' dir='in'>
            <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
              Solicita que o dispositivo execute uma ação. Deve ser respondido com{' '}
              <code>command_ack</code> assim que a execução for finalizada (sucesso ou falha).
            </p>
            <CodeBlock lang='json'>{`{
  "type": "command",
  "payload": {
    "command_id": "42",
    "command_type_id": 5,
    "params": { "mode": "standby" },  // opcional
    "hash": "abc123def"                // opcional
  }
}`}</CodeBlock>
          </MessageCard>

          <MessageCard name='command_ack' dir='out'>
            <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
              Confirma a execução de um <code>command</code>. Quando <code>executed=true</code>,
              o servidor marca o comando como concluído no banco.
            </p>
            <CodeBlock lang='json'>{`{
  "type": "command_ack",
  "payload": {
    "command_id": "42",
    "executed": true,
    "details": { "duration_ms": 250 }   // opcional
  }
}`}</CodeBlock>
          </MessageCard>
        </Section>

        <Section id='close-codes' title='Códigos de fechamento'>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
            O gateway usa códigos customizados na faixa 4xxx para sinalizar motivos de
            encerramento — útil pra implementar lógica de reconexão no firmware.
          </p>
          <div style={{ border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.card, overflow: 'hidden', background: TOKENS.color.card }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 110 }}>Código</th>
                  <th style={thStyle}>Significado</th>
                  <th style={thStyle}>O que fazer</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={tdStyle}><code>1000</code></td>
                  <td style={tdStyle}>Fechamento normal</td>
                  <td style={tdStyle}>Reconectar normalmente</td>
                </tr>
                <tr>
                  <td style={tdStyle}><code>4401</code></td>
                  <td style={tdStyle}>Não autenticado (token inválido ou ausente)</td>
                  <td style={tdStyle}>Reprovisionar o token</td>
                </tr>
                <tr>
                  <td style={tdStyle}><code>4403</code></td>
                  <td style={tdStyle}>Token revogado</td>
                  <td style={tdStyle}>Não reconectar; pedir novo token ao back-office</td>
                </tr>
                <tr>
                  <td style={tdStyle}><code>4429</code></td>
                  <td style={tdStyle}>Conexão duplicada (mesmo dispositivo reconectou)</td>
                  <td style={tdStyle}>Não tentar; provavelmente outra instância está conectada</td>
                </tr>
                <tr>
                  <td style={tdStyle}><code>4500</code></td>
                  <td style={tdStyle}>Erro interno do servidor</td>
                  <td style={tdStyle}>Reconectar com backoff exponencial</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section id='heartbeat' title='Heartbeat / ping'>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
            Duas camadas independentes mantêm a conexão saudável:
          </p>
          <ul style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.7, paddingLeft: 20 }}>
            <li>
              <strong>WebSocket ping/pong</strong> (gerenciado pela lib): o gateway envia{' '}
              <code>ping</code> a cada <strong>25s</strong> e desconecta se não receber{' '}
              <code>pong</code> em <strong>60s</strong>. A maioria das libs responde automaticamente
              — você não precisa fazer nada.
            </li>
            <li>
              <strong>Mensagem <code>heartbeat</code> da aplicação</strong>: enviada pelo
              dispositivo para atualizar <code>last_seen_at</code> no banco. Recomendado a cada
              30s; a janela esperada de silêncio é de 30s antes de ser considerado offline pelo
              monitoramento.
            </li>
          </ul>
        </Section>

        <Section id='playground' title='Playground'>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
            Para testes interativos sem precisar escrever código, o gateway hospeda um
            playground em <code>{wsUrl.replace(/^wss?:/, 'https:')}/playground</code>{' '}
            (em ambientes que tenham a feature habilitada).
          </p>
          <p style={{ color: TOKENS.color.text, fontSize: 14, lineHeight: 1.6 }}>
            Lá você pode escolher entre <strong>Modo Dispositivo</strong> (cola um token
            provisionado e simula uma caixa) e <strong>Modo Cliente Web</strong> (usa o JWT
            da sua sessão pra ver o que o painel recebe em tempo real).
          </p>
        </Section>

        <Section id='troubleshooting' title='Troubleshooting'>
          <div className='accordion' id='troubleshooting-accordion'>
            {[
              {
                id: 'tb-1', q: 'Recebo 4401 ao conectar', a: (
                  <>
                    <p>Significa que o JWT não pôde ser validado. Verifique:</p>
                    <ul>
                      <li>O token não expirou (embora os tokens de dispositivo sejam longos)</li>
                      <li>O <code>jti</code> ainda existe na tabela <code>device_tokens</code></li>
                      <li>O <code>bluetooth_id</code> no <code>sub</code> corresponde a uma caixa válida</li>
                      <li>A URL inclui <code>?token=...</code> ou o cabeçalho <code>Authorization</code></li>
                    </ul>
                  </>
                ),
              },
              {
                id: 'tb-2', q: 'Recebo 4429 sempre que reconecto', a: (
                  <p>
                    Indica que outra instância do mesmo dispositivo já está conectada. Causas
                    comuns: dois firmwares com o mesmo token rodando, ou reconexão antes do
                    timeout do servidor expirar a conexão anterior (60s). Aguarde ~70s ou
                    revise se há outra instância ativa.
                  </p>
                ),
              },
              {
                id: 'tb-3', q: 'Envio alarm_event e o painel não atualiza', a: (
                  <>
                    <p>Verifique:</p>
                    <ul>
                      <li>O <code>alarm_type</code> está entre os valores aceitos (veja a referência)</li>
                      <li>A caixa pertence à empresa do usuário logado no painel</li>
                      <li>O painel está com a tela de sensores ativa (a conexão WS é específica dela)</li>
                    </ul>
                  </>
                ),
              },
              {
                id: 'tb-4', q: 'Mensagens minhas são rejeitadas', a: (
                  <p>
                    O parser do gateway é estrito: a mensagem precisa ser um JSON válido com{' '}
                    <code>type</code> (string) e <code>payload</code> (objeto). Frames binários
                    não são suportados; mande tudo como texto.
                  </p>
                ),
              },
            ].map(({ id, q, a }) => (
              <div key={id} className='accordion-item' style={{ border: `1px solid ${TOKENS.color.border}`, borderRadius: TOKENS.radius.card, marginBottom: TOKENS.spacing.sm, overflow: 'hidden', background: TOKENS.color.card }}>
                <h2 className='accordion-header' style={{ margin: 0 }}>
                  <button
                    className='accordion-button collapsed'
                    type='button'
                    data-bs-toggle='collapse'
                    data-bs-target={`#${id}`}
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: TOKENS.color.text,
                      background: TOKENS.color.card,
                      padding: '12px 16px',
                    }}
                  >
                    {q}
                  </button>
                </h2>
                <div id={id} className='accordion-collapse collapse' data-bs-parent='#troubleshooting-accordion'>
                  <div className='accordion-body' style={{ fontSize: 13, color: TOKENS.color.text, lineHeight: 1.6, padding: '12px 16px', borderTop: `1px solid ${TOKENS.color.divider}` }}>
                    {a}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        <div style={{ marginTop: TOKENS.spacing.xxl, paddingTop: TOKENS.spacing.lg, borderTop: `1px solid ${TOKENS.color.border}` }}>
          <Link
            to='/dashboard'
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              color: '#fff',
              background: TOKENS.color.action,
              borderRadius: TOKENS.radius.card,
              textDecoration: 'none',
            }}
          >
            <ChevronLeft size={14} />
            Voltar para a Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export { TutorialWebSocketPage };
